import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, Camera, MapPin, DollarSign, Bed, Bath, Users,
  Clock, Zap, CheckCircle, Loader2, Trash2, Plus, AlertTriangle, Crown, Key, Accessibility,
  Shield, UserCheck, Building2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import Layout from '../components/Layout';
import { propertiesApi, subscriptionsApi, ownerPolicyApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const STEPS = [
  { id: 'basics', title: 'Basics', icon: Camera },
  { id: 'location', title: 'Location', icon: MapPin },
  { id: 'details', title: 'Details', icon: Bed },
  { id: 'pricing', title: 'Pricing', icon: DollarSign },
  { id: 'photos', title: 'Photos', icon: Camera },
];

const AMENITIES = [
  'WiFi', 'Fast WiFi', 'Kitchen', 'Parking', 'Laundry', 'Air Conditioning',
  'Heating', 'Pool', 'Gym', 'Pet Friendly', 'Furnished', 'Keyless Entry',
  'Dedicated Workspace', 'Blackout Curtains', 'EV Charger', 'King Size Bed',
  'TV', 'Outdoor Space', 'Private Entrance'
];

const ACCESSIBILITY_FEATURES = [
  'Wheelchair Accessible', 'Elevator', 'Close to Public Transportation', 'Disabled Parking'
];

const CreateListingPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const [attestation, setAttestation] = useState({ has_attested: false });
  const [attestationChecked, setAttestationChecked] = useState(false);
  const [attestationSubmitting, setAttestationSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    property_type: '',
    address: '',
    city: 'Los Angeles',
    zip_code: '',
    lat: 34.0522,
    lng: -118.2437,
    price_per_month: '',
    deposit_amount: '',
    min_stay_days: 30,
    max_guests: 1,
    bedrooms: 1,
    bathrooms: 1,
    amenities: [],
    accessibility_features: [],
    house_rules: [],
    photos: [],
    instant_booking: false
  });

  const [newRule, setNewRule] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'owner')) {
      toast.error('Only property owners can create listings');
      navigate('/dashboard');
      return;
    }
    
    // Check subscription and attestation status
    const checkRequirements = async () => {
      try {
        const [subRes, attRes] = await Promise.all([
          subscriptionsApi.getMySubscription(),
          ownerPolicyApi.getAttestation()
        ]);
        setSubscription(subRes.data);
        setAttestation(attRes.data);
      } catch (error) {
        console.error('Failed to check requirements:', error);
      } finally {
        setSubscriptionChecked(true);
        setAttestationChecked(true);
      }
    };
    
    if (user?.role === 'owner') {
      checkRequirements();
    }
    
    if (id) {
      fetchProperty();
    }
  }, [id, user, authLoading, navigate]);

  const fetchProperty = async () => {
    setLoading(true);
    try {
      const response = await propertiesApi.getById(id);
      const prop = response.data;
      setFormData({
        title: prop.title,
        description: prop.description,
        property_type: prop.property_type,
        address: prop.address,
        city: prop.city,
        zip_code: prop.zip_code,
        lat: prop.lat,
        lng: prop.lng,
        price_per_month: prop.price_per_month.toString(),
        deposit_amount: prop.deposit_amount.toString(),
        min_stay_days: prop.min_stay_days,
        max_guests: prop.max_guests,
        bedrooms: prop.bedrooms,
        bathrooms: prop.bathrooms,
        amenities: prop.amenities || [],
        accessibility_features: prop.accessibility_features || [],
        house_rules: prop.house_rules || [],
        photos: prop.photos || [],
        instant_booking: prop.instant_booking
      });
    } catch (error) {
      toast.error('Property not found');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleAmenity = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const toggleAccessibility = (feature) => {
    setFormData(prev => ({
      ...prev,
      accessibility_features: prev.accessibility_features.includes(feature)
        ? prev.accessibility_features.filter(f => f !== feature)
        : [...prev.accessibility_features, feature]
    }));
  };

  const addRule = () => {
    if (newRule.trim()) {
      setFormData(prev => ({
        ...prev,
        house_rules: [...prev.house_rules, newRule.trim()]
      }));
      setNewRule('');
    }
  };

  const removeRule = (index) => {
    setFormData(prev => ({
      ...prev,
      house_rules: prev.house_rules.filter((_, i) => i !== index)
    }));
  };

  const addPhoto = () => {
    if (photoUrl.trim()) {
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, photoUrl.trim()]
      }));
      setPhotoUrl('');
    }
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const validateStep = () => {
    switch (currentStep) {
      case 0: // Basics
        if (!formData.title || !formData.description || !formData.property_type) {
          toast.error('Please fill in all required fields');
          return false;
        }
        break;
      case 1: // Location
        if (!formData.address || !formData.zip_code) {
          toast.error('Please provide address and zip code');
          return false;
        }
        break;
      case 2: // Details
        // Optional fields
        break;
      case 3: // Pricing
        if (!formData.price_per_month || parseFloat(formData.price_per_month) <= 0) {
          toast.error('Please enter a valid monthly price');
          return false;
        }
        break;
      case 4: // Photos
        // Optional for MVP
        break;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      if (currentStep < STEPS.length - 1) {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        price_per_month: parseFloat(formData.price_per_month),
        deposit_amount: parseFloat(formData.deposit_amount) || 0,
        lat: parseFloat(formData.lat),
        lng: parseFloat(formData.lng)
      };

      if (id) {
        await propertiesApi.update(id, payload);
        toast.success('Listing updated successfully!');
      } else {
        await propertiesApi.create(payload);
        toast.success('Listing created successfully!');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save listing');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteAttestation = async () => {
    setAttestationSubmitting(true);
    try {
      await ownerPolicyApi.completeAttestation();
      setAttestation({ has_attested: true, attested_at: new Date().toISOString() });
      toast.success('Owner attestation completed successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete attestation');
    } finally {
      setAttestationSubmitting(false);
    }
  };

  if (authLoading || loading || !subscriptionChecked || !attestationChecked) {
    return (
      <Layout>
        <div className="h-96 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-trust-blue" />
        </div>
      </Layout>
    );
  }

  // Show subscription required message if no active subscription and creating new listing
  if (!id && subscription?.status !== 'active') {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-100 rounded-full mb-6">
              <AlertTriangle className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 mb-4">
              Subscription Required
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              To publish listings on Stay Pair Homes, you need an active subscription.
              Choose a plan that fits your needs and start connecting with renters today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/pricing">
                <Button size="lg" className="bg-trust-blue hover:bg-trust-blue-hover text-white">
                  <Crown className="w-5 h-5 mr-2" />
                  View Subscription Plans
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button size="lg" variant="outline">
                  Back to Dashboard
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Show owner attestation required message if not attested and creating new listing
  if (!id && !attestation?.has_attested) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-16">
          <div className="bg-white rounded-2xl border border-slate-200 p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-trust-blue/10 rounded-full mb-6">
                <UserCheck className="w-10 h-10 text-trust-blue" />
              </div>
              <h1 className="font-heading text-3xl font-bold text-slate-900 mb-4">
                Owner Verification Required
              </h1>
              <p className="text-lg text-slate-600">
                Before listing your property, please confirm you are the direct property owner.
              </p>
            </div>

            {/* Policy Box */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <Building2 className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-amber-800 mb-2">Owner-Only Listings Policy</h3>
                  <p className="text-amber-700 text-sm mb-4">
                    Stay Pair Homes is a platform exclusively for direct property owners. Property management 
                    companies, agencies, and third-party representatives are not permitted to list properties.
                  </p>
                  <ul className="text-amber-700 text-sm space-y-1">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>You must be the legal owner of the property you list</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>You may not list properties on behalf of others</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>Violations will result in listing removal and account suspension</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Attestation Checkbox */}
            <div className="bg-slate-50 rounded-xl p-6 mb-8">
              <div className="flex items-start gap-4">
                <Checkbox 
                  id="attestation-checkbox"
                  checked={false}
                  disabled
                  className="mt-1"
                />
                <label htmlFor="attestation-checkbox" className="text-slate-700">
                  <span className="font-medium">I confirm that:</span>
                  <ul className="mt-2 space-y-1 text-sm text-slate-600">
                    <li>• I am the legal owner of the property I am about to list</li>
                    <li>• I am not listing on behalf of a property management company or agency</li>
                    <li>• I understand that false attestation will result in account termination</li>
                  </ul>
                </label>
              </div>
            </div>

            {/* Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="text-center p-4">
                <Shield className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">Trusted Community</p>
                <p className="text-xs text-slate-500">Direct owner-renter connections</p>
              </div>
              <div className="text-center p-4">
                <DollarSign className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">No Middleman Fees</p>
                <p className="text-xs text-slate-500">Fair pricing without markup</p>
              </div>
              <div className="text-center p-4">
                <Zap className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">Faster Responses</p>
                <p className="text-xs text-slate-500">Communicate directly with owners</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-trust-blue hover:bg-trust-blue-hover text-white"
                onClick={handleCompleteAttestation}
                disabled={attestationSubmitting}
                data-testid="complete-attestation-btn"
              >
                {attestationSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <UserCheck className="w-5 h-5 mr-2" />
                    I Confirm &amp; Accept
                  </>
                )}
              </Button>
              <Link to="/dashboard">
                <Button size="lg" variant="outline">
                  Back to Dashboard
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showFooter={false}>
      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="create-listing-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-slate-900">
            {id ? 'Edit Listing' : 'Create New Listing'}
          </h1>
          <p className="text-slate-600 mt-2">
            {id ? 'Update your property details' : 'List your property in just a few steps'}
          </p>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
          {STEPS.map((step, idx) => (
            <div key={step.id} className="flex items-center">
              <button
                onClick={() => idx < currentStep && setCurrentStep(idx)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                  idx === currentStep
                    ? 'bg-trust-blue text-white'
                    : idx < currentStep
                    ? 'bg-green-100 text-green-700'
                    : 'bg-slate-100 text-slate-400'
                }`}
              >
                {idx < currentStep ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <step.icon className="w-4 h-4" />
                )}
                <span className="text-sm font-medium whitespace-nowrap">{step.title}</span>
              </button>
              {idx < STEPS.length - 1 && (
                <div className={`w-8 h-0.5 mx-1 ${idx < currentStep ? 'bg-green-300' : 'bg-slate-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8">
          {/* Step 0: Basics */}
          {currentStep === 0 && (
            <div className="space-y-6">
              <div>
                <Label htmlFor="title">Listing Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  placeholder="e.g., Sunny Studio in Silver Lake"
                  className="mt-1"
                  data-testid="listing-title"
                />
              </div>

              <div>
                <Label htmlFor="property_type">Property Type *</Label>
                <Select 
                  value={formData.property_type} 
                  onValueChange={(v) => updateField('property_type', v)}
                >
                  <SelectTrigger className="mt-1" data-testid="property-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="room">Private Room</SelectItem>
                    <SelectItem value="adu">ADU (Accessory Dwelling Unit)</SelectItem>
                    <SelectItem value="guesthouse">Guesthouse</SelectItem>
                    <SelectItem value="apartment">Apartment</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder="Describe your property, neighborhood, and what makes it special..."
                  rows={5}
                  className="mt-1"
                  data-testid="listing-description"
                />
              </div>
            </div>
          )}

          {/* Step 1: Location */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <Label htmlFor="address">Street Address *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => updateField('address', e.target.value)}
                  placeholder="123 Main Street"
                  className="mt-1"
                  data-testid="listing-address"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => updateField('city', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="zip_code">ZIP Code *</Label>
                  <Input
                    id="zip_code"
                    value={formData.zip_code}
                    onChange={(e) => updateField('zip_code', e.target.value)}
                    placeholder="90001"
                    className="mt-1"
                    data-testid="listing-zip"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="lat">Latitude</Label>
                  <Input
                    id="lat"
                    type="number"
                    step="0.0001"
                    value={formData.lat}
                    onChange={(e) => updateField('lat', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="lng">Longitude</Label>
                  <Input
                    id="lng"
                    type="number"
                    step="0.0001"
                    value={formData.lng}
                    onChange={(e) => updateField('lng', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
              <p className="text-sm text-slate-500">
                Tip: Get coordinates from Google Maps by right-clicking on your location.
              </p>
            </div>
          )}

          {/* Step 2: Details */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="bedrooms">Bedrooms</Label>
                  <Select 
                    value={formData.bedrooms.toString()} 
                    onValueChange={(v) => updateField('bedrooms', parseInt(v))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="bathrooms">Bathrooms</Label>
                  <Select 
                    value={formData.bathrooms.toString()} 
                    onValueChange={(v) => updateField('bathrooms', parseFloat(v))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 1.5, 2, 2.5, 3].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="max_guests">Max Guests</Label>
                  <Select 
                    value={formData.max_guests.toString()} 
                    onValueChange={(v) => updateField('max_guests', parseInt(v))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="mb-3 block">Amenities</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {AMENITIES.map(amenity => (
                    <div key={amenity} className="flex items-center gap-2">
                      <Checkbox
                        id={amenity}
                        checked={formData.amenities.includes(amenity)}
                        onCheckedChange={() => toggleAmenity(amenity)}
                        data-testid={`amenity-${amenity.toLowerCase().replace(/\s+/g, '-')}`}
                      />
                      <label htmlFor={amenity} className="text-sm text-slate-700 cursor-pointer">
                        {amenity}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="mb-3 block flex items-center gap-2">
                  <Accessibility className="w-4 h-4 text-slate-500" />
                  Accessibility Features
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  {ACCESSIBILITY_FEATURES.map(feature => (
                    <div key={feature} className="flex items-center gap-2">
                      <Checkbox
                        id={`accessibility-${feature}`}
                        checked={formData.accessibility_features.includes(feature)}
                        onCheckedChange={() => toggleAccessibility(feature)}
                        data-testid={`accessibility-${feature.toLowerCase().replace(/\s+/g, '-')}`}
                      />
                      <label htmlFor={`accessibility-${feature}`} className="text-sm text-slate-700 cursor-pointer">
                        {feature}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="mb-3 block">House Rules</Label>
                <div className="flex gap-2 mb-3">
                  <Input
                    value={newRule}
                    onChange={(e) => setNewRule(e.target.value)}
                    placeholder="Add a house rule..."
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRule())}
                  />
                  <Button type="button" onClick={addRule} variant="outline">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.house_rules.map((rule, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-soft-gray rounded-lg p-2">
                      <span className="flex-1 text-sm">{rule}</span>
                      <Button 
                        type="button"
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeRule(idx)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Pricing */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <Label htmlFor="price_per_month">Monthly Rent (USD) *</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="price_per_month"
                    type="number"
                    value={formData.price_per_month}
                    onChange={(e) => updateField('price_per_month', e.target.value)}
                    placeholder="1500"
                    className="pl-10"
                    data-testid="listing-price"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="deposit_amount">Security Deposit (USD)</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="deposit_amount"
                    type="number"
                    value={formData.deposit_amount}
                    onChange={(e) => updateField('deposit_amount', e.target.value)}
                    placeholder="0"
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="min_stay_days">Minimum Stay (Days)</Label>
                <Select 
                  value={formData.min_stay_days.toString()} 
                  onValueChange={(v) => updateField('min_stay_days', parseInt(v))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="60">60 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                    <SelectItem value="180">180 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between p-4 bg-soft-gray rounded-xl">
                <div>
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-trust-blue" />
                    <span className="font-medium text-slate-900">Instant Booking</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">
                    Allow renters to book without approval
                  </p>
                </div>
                <Switch
                  checked={formData.instant_booking}
                  onCheckedChange={(v) => updateField('instant_booking', v)}
                  data-testid="instant-booking"
                />
              </div>
            </div>
          )}

          {/* Step 4: Photos */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div>
                <Label className="mb-3 block">Property Photos</Label>
                <p className="text-sm text-slate-500 mb-4">
                  Add photo URLs for your property. Quality photos help attract more renters.
                </p>
                
                <div className="flex gap-2 mb-4">
                  <Input
                    value={photoUrl}
                    onChange={(e) => setPhotoUrl(e.target.value)}
                    placeholder="Paste image URL..."
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPhoto())}
                  />
                  <Button type="button" onClick={addPhoto} variant="outline">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {formData.photos.map((photo, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={photo}
                        alt={`Photo ${idx + 1}`}
                        className="w-full aspect-video object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(idx)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {formData.photos.length === 0 && (
                    <div className="col-span-full bg-soft-gray rounded-lg p-8 text-center">
                      <Camera className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                      <p className="text-slate-500">No photos added yet</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 0}
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          {currentStep < STEPS.length - 1 ? (
            <Button
              onClick={handleNext}
              className="bg-trust-blue hover:bg-trust-blue-hover rounded-full"
              data-testid="next-step"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-coral hover:bg-coral-hover text-white rounded-full"
              data-testid="submit-listing"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  {id ? 'Update Listing' : 'Publish Listing'}
                  <CheckCircle className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CreateListingPage;
